/**
 * Helpers for the manual-screenshot-upload UX (v0.6.0).
 *
 * Kept tiny and dependency-free so they can be unit-tested without DOM/jsdom
 * setup. The interesting work — drag/drop, paste, file picker — lives in
 * Form.tsx.
 */

export const ALLOWED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
] as const

export const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024 // 10 MB

export type ScreenshotValidationError =
  | { kind: 'type' }
  | { kind: 'size'; maxMb: number }

export function validateScreenshotFile(file: File): ScreenshotValidationError | null {
  if (
    !ALLOWED_IMAGE_TYPES.includes(
      file.type as (typeof ALLOWED_IMAGE_TYPES)[number],
    )
  ) {
    return { kind: 'type' }
  }
  if (file.size > MAX_SCREENSHOT_BYTES) {
    return { kind: 'size', maxMb: MAX_SCREENSHOT_BYTES / (1024 * 1024) }
  }
  return null
}

/**
 * Truncate a URL while keeping the start and end visible. Used in the form's
 * "Vous étiez ici" footer so the user can verify the URL we'll send without
 * the long path overflowing the modal.
 *
 *   "https://app.example.com/very/long/deep/path?a=1&b=2"  (53 chars, max 30)
 *   →  "https://app.examp…h?a=1&b=2"
 */
export function truncateUrl(url: string, maxLength = 80): string {
  if (url.length <= maxLength) return url
  const keepStart = Math.floor((maxLength - 1) / 2)
  const keepEnd = maxLength - 1 - keepStart
  return `${url.slice(0, keepStart)}…${url.slice(url.length - keepEnd)}`
}

/**
 * Return `url` only if its scheme is in the safe-for-href allowlist —
 * `https:` always, `http:` for localhost only. Otherwise return `undefined`,
 * which renders as a non-clickable element (and React/Preact omit the href
 * attribute entirely). This stops a malicious widget user from planting
 * `javascript:`, `data:`, `vbscript:`, or `blob:` URLs in their report's
 * `page_url` / `screenshot_url` and getting an operator's click to execute
 * arbitrary code in the widget host's origin.
 */
export function safeExternalHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return undefined
  }
  if (parsed.protocol === 'https:') return parsed.toString()
  if (
    parsed.protocol === 'http:' &&
    (parsed.hostname === 'localhost' ||
      parsed.hostname === '127.0.0.1' ||
      parsed.hostname === '[::1]')
  ) {
    return parsed.toString()
  }
  return undefined
}

